import { useCallback, useMemo, useSyncExternalStore } from "react";

export type DesktopPreferences = {
  wallpaper: "portal" | "graphite";
  iconDensity: "comfortable" | "compact";
  reduceMotion: boolean;
};
export const defaultDesktopPreferences: DesktopPreferences = { wallpaper: "portal", iconDensity: "comfortable", reduceMotion: false };
const changed = "base-sul-os:desktop-preferences";
const volatile = new Map<string, string>();
export const desktopPreferencesKey = (userId: string) => `base-sul-os:desktop-preferences:v1:${userId}`;

function snapshot(userId: string) {
  const key = desktopPreferencesKey(userId);
  if (volatile.has(key)) return volatile.get(key)!;
  try { return localStorage.getItem(key); } catch { return null; }
}

export function parseDesktopPreferences(raw: string | null): DesktopPreferences {
  try {
    const value: unknown = raw ? JSON.parse(raw) : null;
    if (!value || typeof value !== "object" || Array.isArray(value)) return defaultDesktopPreferences;
    const source = value as Record<string, unknown>;
    return {
      wallpaper: source.wallpaper === "graphite" ? "graphite" : "portal",
      iconDensity: source.iconDensity === "compact" ? "compact" : "comfortable",
      reduceMotion: source.reduceMotion === true,
    };
  } catch { return defaultDesktopPreferences; }
}

export function readDesktopPreferences(userId: string) { return parseDesktopPreferences(snapshot(userId)); }

/** No user data leaves the device. Storage failures do not break the desktop. */
export function writeDesktopPreferences(userId: string, patch: Partial<DesktopPreferences>) {
  const key = desktopPreferencesKey(userId);
  const next = JSON.stringify(parseDesktopPreferences(JSON.stringify({ ...readDesktopPreferences(userId), ...patch })));
  let saved = true;
  try { localStorage.setItem(key, next); volatile.delete(key); }
  catch { volatile.set(key, next); saved = false; }
  window.dispatchEvent(new Event(changed));
  return saved;
}

function subscribe(listener: () => void) {
  window.addEventListener(changed, listener);
  window.addEventListener("storage", listener);
  return () => { window.removeEventListener(changed, listener); window.removeEventListener("storage", listener); };
}

export function useDesktopPreferences(userId: string) {
  const read = useCallback(() => snapshot(userId), [userId]);
  const raw = useSyncExternalStore(subscribe, read, () => null);
  const preferences = useMemo(() => parseDesktopPreferences(raw), [raw]);
  const update = useCallback((patch: Partial<DesktopPreferences>) => writeDesktopPreferences(userId, patch), [userId]);
  return { preferences, update };
}
